import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useSecureCable from "../utils/useSecureCable";
import CLIENT_TAB_ID from "../utils/clientTabId";
import { stage } from "../reducers/stage.reducer";
import { stageConstants } from "../constants/stage.constants";
import { CONTENT_GROUP_ACTION, CONTENT_GROUP_CALENDAR, userConstants } from "../constants";
import { content_groupActions } from "../actions";

function useWorkspaceActionCable({
    workspace_id
}) {
    // Variables
    const dispatch = useDispatch();
    const channelName = `WorkspaceChannel`;
    const secureCable = useSecureCable({refreshUrl: '/api/v1/action_cable/refresh_workspace_token', workspace_id});

    // Refs
    const activeSubRef = useRef(null);
    const subscriptionRef = useRef(null);
    
    // State
    const [connected, setConnected] = useState(false);
    const [authToken, setAuthToken] = useState(null); // TODO: Use token for authentication and not use login token

    // Redux
    const { getCommonData } = useSelector(state => state.dashboard);
    const user_id = getCommonData?.current_user?.id;

    // Functions
    const handleReceivedMessage = useCallback((result) => {
        const { data, type, client_id: broadcaster_client_id } = result.data;
        // Only the originating tab already holds the new order optimistically.
        const isOwnEcho = broadcaster_client_id === CLIENT_TAB_ID;
        console.log('WorkspaceChannel message received:', result);
 
        switch(type) {
            case 'sort':
                // Handle sort action received
                dispatch({ type: stageConstants.UPDATE_STAGES_CARD_COUNT, payload: data.stages });
                if (isOwnEcho) {
                    dispatch({ type: CONTENT_GROUP_ACTION.UPDATE_CARD_ITEM_AT_LIST, payload: data.updated_card });
                } else {
                    //dispatch({ type: stageConstants.SORT_STAGE_AND_CARDS, payload: data.stages });
                    const timeoutId = setTimeout(() => {
                        dispatch({ type: stageConstants.STAGES_TO_RELOAD, payload: data.reload_stages });
                    }, 1500);
                    return () => clearTimeout(timeoutId);
                }
                break;
            case 'card_create':
                dispatch({ type: stageConstants.UPDATE_STAGES_CARD_COUNT, payload: [data.stage] });
                dispatch({ type: stageConstants.ADD_ITEM_TO_STAGE, payload: data });
                dispatch({ type: CONTENT_GROUP_CALENDAR.ADD_CALENDAR_CARDS, payload: [data.calendar_data] });
                break;
            case 'card_update':
                dispatch({ type: CONTENT_GROUP_ACTION.UPDATE_CONTENT_GROUP_POPUP, payload: data.popup_data });
                dispatch({ type: stageConstants.UPDATE_STAGES_CARD_COUNT, payload: data.stages_to_reload });
                dispatch({ type: CONTENT_GROUP_CALENDAR.UPDATE_CALENDAR_CARD, payload: data.calendar_data });

                if (data.stages_to_reload && data.stages_to_reload.length > 1) {
                    const timeoutId = setTimeout(() => {
                        dispatch({ type: stageConstants.STAGES_TO_RELOAD, payload: data.stages_to_reload });
                    }, 1000);
                    return () => clearTimeout(timeoutId);
                } else {
                    dispatch({ type: CONTENT_GROUP_ACTION.UPDATE_CARD_ITEM_AT_LIST, payload: data.list_data });
                }
                break;
            case 'card_assign_user':
                dispatch({ type: CONTENT_GROUP_ACTION.UPDATE_CONTENT_GROUP_ASSIGNED_USERS, payload: data });
                break;
            case 'card_bulk_create':
                dispatch({ type: stageConstants.UPDATE_STAGES_CARD_COUNT, payload: [data.stage] });
                dispatch({ type: stageConstants.ADD_ITEMS_TO_STAGE, payload: data });
                break;
            case 'card_recycle_bin':
                dispatch({ type: stageConstants.UPDATE_STAGES_CARD_COUNT, payload: [data.stage] });
                dispatch({ type: stageConstants.REMOVE_ITEM_FROM_STAGE, payload: data });
                break;
            case 'card_assign_label':
                dispatch({ type: CONTENT_GROUP_ACTION.ADD_LABEL_TO_CARD, payload: data });
                break;
            case 'card_remove_label':
                dispatch({ type: CONTENT_GROUP_ACTION.REMOVE_LABEL_FROM_CARD, payload: data });
                break;
            case 'card_approval_update':
                dispatch({ type: CONTENT_GROUP_ACTION.UPDATE_CARD_STAGE_INFO, payload: data });

                break;
            case 'contents_create':
                dispatch({ type: CONTENT_GROUP_ACTION.ADD_ATTACHMENTS_TO_CARD, payload: data });
                const { card } = data || {};

                if (card) {
                    dispatch(content_groupActions.replaceContentImageLazeLoading(card.workspace_id, [card.id]));
                }
                break;
            case 'contents_update':
                dispatch({ type: CONTENT_GROUP_ACTION.UPDATE_ATTACHMENTS_IN_CARD, payload: data });
                const { card: updatedCard } = data || {};

                if (updatedCard) {
                    dispatch(content_groupActions.replaceContentImageLazeLoading(updatedCard.workspace_id, [updatedCard.id]));
                }
                break;
            case 'list_update':
                dispatch({ type: CONTENT_GROUP_ACTION.UPDATE_CARD_LIST_INFO, payload: data });
                break;
            case 'contents_destroy_multi':
                dispatch({ type: CONTENT_GROUP_ACTION.REMOVE_ATTACHMENTS_FROM_CARD, payload: data });
                const { card: cardToBeRefreshed } = data || {};

                if (cardToBeRefreshed) {
                    dispatch(content_groupActions.replaceContentImageLazeLoading(cardToBeRefreshed.workspace_id, [cardToBeRefreshed.id]));
                }
                break;
            case 'stage_create':
                dispatch({
                    type: userConstants.CREATE_STAGE,
                    payload: data
                });
                break;
            case 'stage_update':
                const is_guest = data?.is_guest || false;
                const visible_to_client = data?.stage?.visible_to_client || false;

                if (is_guest && !visible_to_client) {
                    dispatch({ type: userConstants.STAGE_DELETE_REQUEST, payload: { data: data.stage } });
                } else {
                    dispatch({
                        type: stageConstants.UPDATE_STAGE,
                        payload: { data: { data: data.stage } }
                    });

                    dispatch({ type: userConstants.STAGE_UPDATE_REQUEST, payload: { data: data.stage } });

                    const timeoutId = setTimeout(() => {
                        dispatch({ type: stageConstants.STAGES_TO_RELOAD, payload: [data.stage] });
                    }, 1000);
                    return () => clearTimeout(timeoutId);
                }

                break;
            case 'stage_delete':
                dispatch({ type: userConstants.STAGE_DELETE_REQUEST, payload: { data } });
                break;
            case 'update_unread_comments':
                dispatch({ type: CONTENT_GROUP_ACTION.UPDATE_CARD_UNREAD_COMMENTS, payload: data });
                break;
            default:
                console.error('Unknown message type:', type);
        }
    }, [user_id, workspace_id]);

    // Effects
    useEffect(() => {
        if (!secureCable || !workspace_id || !user_id) return;


        const subscriptionKey = `${channelName}-${workspace_id}-${user_id}`;
        if (activeSubRef.current === subscriptionKey) {
            console.log(`🔄 Subscription already active for ${subscriptionKey}`);
            return;
        }
        
        activeSubRef.current = subscriptionKey;

        const subscribe = () => {
            const channel = { channel: channelName, workspace_id };

            if (subscriptionRef.current) secureCable.subscriptions.remove(subscriptionRef.current);

            subscriptionRef.current = secureCable.subscriptions.create(channel, {
            connected: () => {
                console.log(`✅ Connected to ${channelName}`);
                setConnected(true);
            },
            disconnected: () => {
                // ActionCable's ConnectionMonitor reopens and replays this
                // subscription on its own; driving that by hand here only
                // fought it and thrashed the socket.
                console.warn(`⚠️ Disconnected from ${channelName}`);
                setConnected(false);
            },
            rejected: () => {
                setConnected(false);
                console.error(`❌ Subscription rejected by ${channelName}`);
            },
            received: handleReceivedMessage,
            });

            console.log(`🧠 Subscribed to ${channelName}:`, channel);
            return true;
        };

        subscribe();

        return () => {
            if (subscriptionRef.current) secureCable.subscriptions.remove(subscriptionRef.current);
            subscriptionRef.current = null;
            console.log(`🧹 Unsubscribed from ${channelName}`);
            activeSubRef.current = null;
        };
    }, [workspace_id, user_id,secureCable]);

    return { connected };
}

export default useWorkspaceActionCable;